import { Trans } from '@lingui/react/macro';
import {
  Desktop as DesktopIcon,
  Import,
  DeviceMobile,
} from '@remarkable/ark-icons';
import { Typography } from '@remarkable/ark-web';
import { ButtonClicked } from 'src/ampli';
import { tracker } from 'src/analytics/tracker';
import { ComponentLocations } from 'src/analytics/trackingTypes';
import appStoreLogo from 'src/assets/img/app-store.svg';
import googlePlayLogo from 'src/assets/img/google-play.svg';
import { Button, CardContent, Sheet } from 'src/components';
import { APPLE_ITUNES_URL, GOOGLE_PLAY_URL } from '../utils/constants';
import { getDesktopLink, getMobileLink } from '../utils/utils';
import { DeviceTable } from './DeviceTable';

export const Apps = () => {
  return (
    <div className="flex flex-col gap-16 lm:gap-24">
      <Sheet>
        <CardContent className="my-12 flex flex-row items-center justify-between lm:mt-0">
          <div className="flex flex-row items-center">
            <DesktopIcon size={32} className="mr-16" />
            <Typography variant="heading-subtle-2xl" as="h2">
              <Trans>Desktop app</Trans>
            </Typography>
          </div>
          <Button
            className="hidden md:flex"
            as="a"
            variant="primary"
            size="medium"
            to={getDesktopLink()}
            analytics={{
              action: 'download desktop app',
              location: ComponentLocations.DEVICE.APPS,
              text: 'Download',
            }}
          >
            <Import size={24} />
            <span>
              <Trans>Download</Trans>
            </span>
          </Button>
        </CardContent>

        <CardContent borderTop className="flex flex-col gap-24 text-left">
          <p>
            <Trans>
              Use the reMarkable desktop app to view, import, organize, and
              present your content with Screen Share.
            </Trans>
          </p>
          <Button
            className="md:hidden"
            as="a"
            size="medium"
            variant="tertiary"
            to={getDesktopLink()}
            data-cy="download-desktop-app-button"
          >
            <Import size={24} />
            <span>
              <Trans>Download</Trans>
            </span>
          </Button>
        </CardContent>
      </Sheet>
      <Sheet>
        <CardContent className="my-12 flex flex-row items-center justify-between lm:mt-0">
          <div className="flex flex-row items-center">
            <DeviceMobile size={32} className="mr-16" />
            <Typography variant="heading-subtle-2xl" as="h2">
              <Trans>Mobile app</Trans>
            </Typography>
          </div>
          <div data-cy="download-mobile-app-button">
            <div className="hidden md:flex md:flex-row md:gap-16">
              <DownloadLink link={APPLE_ITUNES_URL} />
              <DownloadLink link={GOOGLE_PLAY_URL} />
            </div>
            <div className="md:hidden">
              <DownloadLink link={getMobileLink()} />
            </div>
          </div>
        </CardContent>
        <CardContent borderTop className="text-left">
          <p>
            <Trans>
              Use the mobile app to access your files from wherever you are.
            </Trans>
          </p>
        </CardContent>
      </Sheet>

      <DeviceTable deviceType="apps" />
    </div>
  );
};

interface DownloadLinkProps {
  link: string;
}

const DownloadLink = ({ link }: DownloadLinkProps) => (
  <div>
    <a
      target="_blank"
      rel="noopener noreferrer"
      href={link}
      onClick={() =>
        tracker.trackEvent(
          new ButtonClicked({
            action:
              link === APPLE_ITUNES_URL
                ? 'download ios app'
                : 'download android app',
            component_location: ComponentLocations.DEVICE.APPS,
            text:
              link === APPLE_ITUNES_URL ? 'App Store logo' : 'Google Play logo',
          })
        )
      }
    >
      <img
        className="h-full w-96 md:w-144"
        alt={
          link === APPLE_ITUNES_URL
            ? 'Download on App Store'
            : 'Download on Google Play Store'
        }
        src={link === APPLE_ITUNES_URL ? appStoreLogo : googlePlayLogo}
      />
    </a>
  </div>
);
